import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CreditCard, Loader2, ShieldCheck, XCircle, KeyRound, Eye } from "lucide-react";

export default function VerTarjetaAseguradoraPage() {
  const { token } = useParams();
  const [estado, setEstado] = useState("cargando"); // cargando | valido | invalido | datos
  const [info, setInfo] = useState(null);
  const [otpEnviado, setOtpEnviado] = useState(false);
  const [otp, setOtp] = useState("");
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [cargandoAccion, setCargandoAccion] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cobros/compartido/${token}`)
      .then((r) => { setInfo(r.data); setOtpEnviado(r.data.otp_solicitado); setEstado("valido"); })
      .catch((e) => { setError(e.response?.data?.detail || "Acceso no válido"); setEstado("invalido"); });
  }, [token]);

  const solicitarOtp = async () => {
    setError("");
    try {
      setCargandoAccion(true);
      const r = await axios.post(`${API}/cobros/compartido/${token}/solicitar-otp`);
      setOtpEnviado(true);
      setError("");
      setInfo({ ...info, mensaje_otp: r.data.mensaje });
    } catch (e) { setError(e.response?.data?.detail || "Error al solicitar el código"); } finally { setCargandoAccion(false); }
  };

  const verDatos = async () => {
    setError("");
    try {
      setCargandoAccion(true);
      const r = await axios.post(`${API}/cobros/compartido/${token}/ver`, { otp });
      setDatos(r.data);
      setEstado("datos");
    } catch (e) { setError(e.response?.data?.detail || "Código incorrecto"); } finally { setCargandoAccion(false); }
  };

  return (
    <div className="min-h-screen bg-[#0C314C] flex items-center justify-center p-4" data-testid="ver-tarjeta-aseguradora">
      <Card className="w-full max-w-md">
        <CardContent className="p-6">
          <div className="text-center mb-5">
            {info?.logo_url ? (
              <img src={info.logo_url} alt="QAsesores" className="h-14 mx-auto mb-3 object-contain"
                onError={(e) => { if (!e.target.dataset.fb) { e.target.dataset.fb = "1"; e.target.src = "/logo-qasesores.png"; } else e.target.style.display = "none"; }} />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#0C314C] flex items-center justify-center mx-auto mb-3">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
            )}
            <h1 className="text-xl font-bold">Información de Pago Protegida</h1>
            <p className="text-sm text-slate-500">QAsesores Corredores de Seguros</p>
          </div>

          {estado === "cargando" && <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>}

          {estado === "invalido" && (
            <div className="text-center py-6" data-testid="compartido-invalido">
              <XCircle className="w-12 h-12 mx-auto mb-2 text-red-400" />
              <p className="font-medium">{error || "Este acceso no es válido o expiró"}</p>
              <p className="text-sm text-slate-500 mt-1">Solicite un nuevo envío a su contacto en QAsesores.</p>
            </div>
          )}

          {estado === "valido" && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 border text-sm">
                <p>Tarjeta <strong>•••• {info?.ultimos4}</strong> ({info?.marca}) del cliente <strong>{info?.cliente}</strong></p>
                <p className="text-slate-500 text-xs mt-1">Enviada por {info?.enviado_por}</p>
              </div>
              {!otpEnviado ? (
                <>
                  <p className="text-sm text-slate-600">Para ver la información completa, solicite el código OTP. El código le llegará a <strong>{info?.enviado_por}</strong>, quien se lo suministrará directamente.</p>
                  <Button onClick={solicitarOtp} disabled={cargandoAccion} className="w-full bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="solicitar-otp-btn">
                    {cargandoAccion ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}Solicitar código OTP
                  </Button>
                </>
              ) : (
                <>
                  <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="otp-enviado-msg">
                    Código enviado a {info?.enviado_por}. Contáctele para obtenerlo e ingréselo aquí.
                  </div>
                  <div>
                    <Label>Código OTP (6 dígitos)</Label>
                    <Input inputMode="numeric" maxLength={6} className="text-center text-2xl tracking-[0.5em] font-mono" value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid="otp-input" />
                  </div>
                  <Button onClick={verDatos} disabled={cargandoAccion || otp.length !== 6} className="w-full bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="ver-datos-otp-btn">
                    {cargandoAccion ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}Ver información
                  </Button>
                  <button type="button" onClick={solicitarOtp} className="w-full text-xs text-slate-500 underline" data-testid="reenviar-otp-btn">
                    Solicitar un nuevo código
                  </button>
                </>
              )}
              {error && <p className="text-sm text-red-600" data-testid="compartido-error">{error}</p>}
            </div>
          )}

          {estado === "datos" && datos && (
            <div className="space-y-3" data-testid="datos-tarjeta-aseguradora">
              <div className="p-4 rounded-xl bg-[#0C314C] text-white space-y-2">
                <p className="text-xs opacity-70">{datos.marca} · {datos.cliente}</p>
                <p className="text-xl font-mono tracking-wider">{datos.numero.replace(/(\d{4})(?=\d)/g, "$1 ")}</p>
                <div className="flex justify-between text-sm">
                  <span>{datos.titular}</span>
                  <span>Vence: {datos.vencimiento} · CVV: {datos.cvv}</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1">
                <ShieldCheck className="w-3 h-3" />Esta vista quedó registrada. Para verla de nuevo deberá solicitar otro código OTP.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
